import { motion } from "framer-motion";
import { Briefcase, Sparkles, Compass, Layers } from "lucide-react";

const services = [
  {
    icon: Briefcase,
    no: "01",
    title: "Fractional CPO / CPSO",
    desc: "Embedded product leadership for companies that need clarity, structure and execution discipline at the top — without a full-time hire.",
    bullets: [
      "Product strategy aligned to growth and valuation",
      "Sharper product–market fit and positioning",
      "A scalable product operating model",
    ],
  },
  {
    icon: Sparkles,
    no: "02",
    title: "AI Product Strategy & Innovation",
    desc: "Move past AI experiments. I help leadership teams turn AI capability into compounding product advantage and new revenue.",
    bullets: [
      "AI-first product strategy and roadmap",
      "Embedding AI into core SaaS workflows",
      "Predictive analytics and decision intelligence",
      "Workflow and operations automation",
      "High-ROI AI opportunity mapping",
    ],
  },
  {
    icon: Compass,
    no: "03",
    title: "Strategic Advisory",
    desc: "A trusted thought partner to CEOs, founders and boards on the highest-stakes product, AI and growth decisions.",
    bullets: [
      "Product positioning and differentiation",
      "Growth, pricing and roadmap alignment",
      "Investment prioritization and board narrative",
    ],
  },
  {
    icon: Layers,
    no: "04",
    title: "Product Transformation",
    desc: "For organizations modernizing legacy platforms or rebuilding their product function for scale.",
    bullets: [
      "Product and tech-stack audits",
      "Team and capability redesign",
      "Execution frameworks and governance",
    ],
  },
];

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#050505] py-20 sm:py-28 md:py-40 border-t border-[#141414]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
        <div className="grid grid-cols-12 gap-6 md:gap-8 mb-12 md:mb-24 items-end">
          <div className="col-span-12 md:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-[#C5A880]" />
              <span className="text-[11px] font-semibold tracking-[0.3em] uppercase text-[#C5A880]">
                What I Do / 02
              </span>
            </div>
            <h2
              data-testid="services-heading"
              className="font-serif-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light leading-[1.1] lg:leading-[1.05] tracking-tight text-[#F5F5F0]"
            >
              Four ways I plug into your{" "}
              <em className="italic text-[#C5A880]">leadership team</em> — and
              move the needle.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:pl-8">
            <p className="text-[15px] sm:text-base md:text-lg text-[#A3A39A] leading-relaxed">
              Every engagement is shaped to your stage, market and product
              maturity. Pragmatic, commercially grounded, and measured against
              the outcomes that matter to your board.
            </p>
          </div>
        </div>

        {/* Technical 2x2 grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-[#1a1a1a]">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.no}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: (i % 2) * 0.08 }}
                data-testid={`service-card-${s.no}`}
                className="group relative p-6 sm:p-8 md:p-12 border-r border-b border-[#1a1a1a] hover:bg-[#0b0b0b] transition-colors duration-500"
              >
                <div className="flex items-start justify-between mb-8 sm:mb-10">
                  <div className="w-10 h-10 flex items-center justify-center border border-[#2a2a2a] group-hover:border-[#C5A880] group-hover:text-[#C5A880] transition-colors">
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="text-[11px] tracking-[0.3em] text-[#C5A880]">
                    {s.no}
                  </span>
                </div>
                <h3 className="font-serif-display text-2xl sm:text-3xl md:text-4xl font-light text-[#F5F5F0] mb-4 sm:mb-5 leading-tight">
                  {s.title}
                </h3>
                <p className="text-[15px] sm:text-base text-[#A3A39A] leading-relaxed mb-6 sm:mb-8">
                  {s.desc}
                </p>
                <ul className="space-y-3">
                  {s.bullets.map((b, j) => (
                    <li
                      key={j}
                      className="flex items-start gap-3 text-sm sm:text-[15px] text-[#D8D8D0]"
                    >
                      <span className="mt-2 inline-block h-px w-4 bg-[#C5A880] flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
